<template>
  <div class="chat-pane">
    <b-button class="chat-button"
              v-if="chatAvailable"
              :disabled="!chatAvailable"
              @click="togglePane">
      <b-icon-chat-dots-fill/>
    </b-button>
    <b-sidebar v-if="chatAvailable"
               right
               id="chat-sidebar"
               class="chat-sidebar__content"
               body-class="chat-container d-flex flex-column"
               :title="$i18n.gt('HEADING_CHAT')"
               :no-close-on-route-change="true"
               :visible="paneVisible">
      <template v-slot:title>
        <button class="reload text-dark mr-2" :disabled="!chats.length" @click="scrollToEnd">
          <b-icon icon="arrow-down"/>
        </button>
        <button class="reload text-dark mr-2" :disabled="!chats.length" @click="clearChat">
          <b-icon icon="trash"/>
        </button>
        <button class="reload text-dark" @click="loadChat">
          <b-icon icon="arrow-clockwise"/>
        </button>
        Chat
      </template>
      <div class="px-2 py-2 sidebar-content mb-5">
        <spinner-until :condition="!loading">
          <div class="sidebar-transcript">
            <div v-for="(chat,i) in welcome_chats" :key="i">
              <div :class="chatClass(chat.role)" v-html="textToHtml(chat.content)"/>
            </div>
          </div>
        </spinner-until>
      </div>
      <div class="sidebar-input fixed-bottom w-100">
        <spinner-until :condition="!processing">
          <b-form autocomplete="off"
                  @submit="onSubmit">
            <b-input-group size="sm">
              <b-form-textarea v-model="query"
                               :placeholder="$i18n.gt('PLACEHOLDER_CHAT')"
                               rows="1" max-rows="10"
                               class="border border-light"
                               @keydown.enter.prevent="onSubmit"/>
              <b-input-group-append class="border border-light">
                <b-button variant="light" class="send" @click="onSubmit">
                  <!-- https://icons.getbootstrap.com/icons/send/ -->
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-send"
                       viewBox="0 0 16 16">
                    <path
                      d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576 6.636 10.07Zm6.787-8.201L1.591 6.602l4.339 2.76 7.494-7.493Z"/>
                  </svg>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form>
        </spinner-until>
      </div>
    </b-sidebar>
  </div>
</template>

<script>
import Vue from 'vue'
import * as _ from 'lodash'
import $ from 'jquery'
import Utils from '@/providers/Utils'

/**
 * Renders a chat button on the page, which activates a Chat sidebar
 */
export default Vue.extend({
  name: 'Chat',
  data () {
    return {
      welcome: this.$i18n.gt('LABEL_CHAT_WELCOME'),
      chat_placeholder: this.$i18n.gt('PLACEHOLDER_CHAT'),
      chats: [],
      loading: false,
      processing: false,
      query: null,
    }
  },
  computed: {
    chatAvailable () {
      return this.$user.userId()
    },
    disabled () {
      return !this.$user.userId()
    },
    welcome_chats () {
      return [{
        role: 'welcome',
        content: this.welcome
      },
        ...this.chats]
    },
    paneVisible: {
      get () {
        return this.$store.state.chatPane
      },
      set (val) {
        // save & persist
        this.$store.commit('set',
          val
            ? { chatPane: true, helpPane: false, videoPane: false }
            : { chatPane: false })
        this.$store.dispatch('save')
      }
    },
  },
  methods: {
    togglePane () {
      this.paneVisible = !this.paneVisible
    },
    chatClass (role) {
      return 'chat d-inline-flex rounded rounded-lg bg-light mb-2 px-2 py-2 border ' +
        ((role === 'user')
          ? 'float-left border-primary'
          : 'float-right border-secondary')
    },
    async clearChat () {
      if (await this.$bvModal.msgBoxConfirm(this.$i18n.gt('LABEL_CONFIRM_CLEAR_CHAT'))) {
        this.loading = true
        this.$api.chatClear()
          .then(response => {
            this.chats = []
          })
          .catch(error => {
            console.error('ChatPane:clearChat  error=', error)
            this.$utils.handleError(error, 'Unable to clear chat.')
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    loadChat () {
      this.loading = true
      this.$api.getAllItems(this, 'chat')
        .then(items => {
          this.chats = items || []
          setTimeout(() => this.scrollToEnd(), 250)
        })
        .finally(() => {
          this.loading = false
        })
    },
    textToHtml (content) {
      return content.replace(/\n/g, '<br/>')
    },
    scrollToEnd () {
      var $target = $('.chat-container')
      if ($target.length === 1)
        $target.animate({ scrollTop: $target.get(0).scrollHeight }, 'slow')
    },
    onSubmit () {
      if (this.query) {
        // add query
        this.chats.push({
          role: 'user',
          content: this.query,
          created: Utils.mysqlDatetime(new Date())
        })
        // clear local query
        const localQuery = this.query
        this.query = null
        // call API with spinner
        this.processing = true
        this.$api.chatPost(localQuery)
          .then(response => {
            // add responses
            if (Array.isArray(response))
              this.chats = _.concat(this.chats, response) || []
            else
              console.error('ChatPane:onSubmit  unexpected chat response  response=', response)
            // scroll to bottom
            Vue.nextTick(() => this.scrollToEnd())
          })
          .catch(error => {
            console.error('ChatPane:onSubmit  error=', error)
          })
          .finally(() => {
            this.processing = false
          })
      }
    }
  },
  mounted () {
    if (this.chatAvailable)
      this.loadChat()
  }
})
</script>

<style lang="scss">
.chat-pane {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 2;

  header.b-sidebar-header > .close, header.b-sidebar-header > .reload {
    height: 32px;
    width: 32px;
  }

  #chat-sidebar {
    box-shadow: 0 0 20px rgba(0, 0, 0, .1);
    border-left: solid 1px var(--border-color);
    background-color: var(--accent-color-lighten-40-opaque) !important;
    height: calc(100vh - 44px);
  }

  .sidebar-content {
    .sidebar-transcript {
      .chat {
        clear: both;
        max-width: 90%;
      }
    }
  }

  button.close, button.reload {
    border: solid 1px var(--blue-lighten-30);
    background-color: #fff;
    padding: 3px;
    border-radius: 50%;
    cursor: pointer;
    opacity: 1;

    svg {
      color: var(--blue-lighten-30);
    }

    &:hover {
      background-color: var(--btn-bg-hover);
      border-color: var(--btn-bg-hover);
      opacity: 1 !important;

      svg {
        color: #fff !important;
      }
    }
  }

  button.send {
    color: var(base-color);
  }

  .chat-button {
    background: var(--base-color) !important;
    color: #fff !important;
    margin: 0 5px;
    border-radius: 5px 5px 0 0;
    display: inline-block;

    position: absolute;
    right: 50px;
    bottom: 0;
    padding: 7px;

    &:active, &:focus {
      background-color: var(base-color);
    }

    svg {
      font-size: 30px !important;
    }
  }
}
</style>
